import React, {
  JSX,
  KeyboardEvent,
  memo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/material/styles/useTheme';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckIcon from '@mui/icons-material/CheckOutlined';
import { getUserAvatarColors } from 'src/components/NewRenterInterface/utils/avatarColors';

export interface ManagedMember {
  userId: number;
  firstName: string;
  lastName: string;
  displayName: string;
  initials: string;
}

interface MemberCarouselProps {
  members: ManagedMember[];
  selectedUserId: number;
  onSelect: (userId: number) => void;
  /** Set of userIds that have pending actions — renders a red dot on their avatar. */
  membersWithActions?: Set<number>;
  /** When true, action statuses are still loading — suppress the status badge
   *  so the green "no action" checkmark doesn't flash before the real status. */
  actionsLoading?: boolean;
  /** Optional content rendered inside the card below pagination dots. */
  children?: React.ReactNode;
  /** When true, render without the standalone card styling so it can be
   *  embedded inside another Card. */
  disableCard?: boolean;
}

// Approx width of one avatar + gap (44px avatar + 16px gap).
const AVATAR_SLOT_WIDTH = 60;

const cardSx = {
  backgroundColor: 'background.paper',
  borderRadius: '14px',
  boxShadow: '0px 2px 8px rgba(0,0,0,0.10)',
  pt: 1.5,
  pb: 1,
  position: 'relative',
} as const;

const scrollRowSx = {
  overflowX: 'auto',
  pb: 0.5,
  // Extra top padding so scale(1.08) doesn't clip at the card top edge.
  pt: 0.75,
  // Wider horizontal padding so the first/last avatar isn't clipped by the card edge.
  px: 2.5,
  scrollbarWidth: 'none' as const,
  '&::-webkit-scrollbar': { display: 'none' },
} as const;

const initialsTextSx = {
  fontSize: 13,
  fontWeight: 700,
  userSelect: 'none' as const,
} as const;

function getAvatarSx(isSelected: boolean, color: string): object {
  return {
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isSelected ? color : 'transparent',
    border: '2px solid',
    borderColor: color,
    cursor: 'pointer',
    transition: 'background-color 0.15s, transform 0.12s',
    transform: isSelected ? 'scale(1.08)' : 'scale(1)',
    '&:focus-visible': {
      outline: '2px solid',
      outlineColor: color,
      outlineOffset: 2,
    },
  };
}

function getNameSx(isSelected: boolean): object {
  return {
    fontSize: 10,
    fontWeight: isSelected ? 700 : 400,
    color: isSelected ? 'text.primary' : 'text.secondary',
    mt: 0.5,
    whiteSpace: 'nowrap' as const,
    textTransform: 'capitalize' as const,
  };
}

function getLastNameSx(isSelected: boolean): object {
  return {
    fontSize: 10,
    fontWeight: isSelected ? 700 : 400,
    color: isSelected ? 'text.primary' : 'text.secondary',
    mt: 0,
    whiteSpace: 'nowrap' as const,
    textTransform: 'capitalize' as const,
  };
}

function handleAvatarKeyDown(
  e: KeyboardEvent,
  userId: number,
  onSelect: (id: number) => void
): void {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    onSelect(userId);
  }
}

const MemberCarousel = memo(function MemberCarousel({
  members,
  selectedUserId,
  onSelect,
  membersWithActions,
  actionsLoading = false,
  children,
  disableCard = false,
}: MemberCarouselProps): JSX.Element {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  // Active pagination dot: primary.main passes on white; use teal in dark mode.
  const activeDotColor = isDark ? '#1ABC9C' : 'primary.main';

  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollPage, setScrollPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const recalcPages = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const visibleWidth = el.clientWidth;
    if (visibleWidth === 0) {
      return;
    }
    const perPage = Math.max(1, Math.floor(visibleWidth / AVATAR_SLOT_WIDTH));
    setTotalPages(Math.ceil(members.length / perPage));
    setScrollPage(Math.round(el.scrollLeft / visibleWidth));
  }, [members.length]);

  useEffect(() => {
    recalcPages();
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const handleScroll = (): void => {
      const visibleWidth = el.clientWidth;
      if (visibleWidth === 0) {
        return;
      }
      setScrollPage(Math.round(el.scrollLeft / visibleWidth));
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', recalcPages);
    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', recalcPages);
    };
  }, [recalcPages]);

  const scrollToPage = useCallback((page: number): void => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    el.scrollTo({ left: page * el.clientWidth, behavior: 'smooth' });
  }, []);

  const handlePrev = useCallback((): void => {
    scrollToPage(Math.max(0, scrollPage - 1));
  }, [scrollPage, scrollToPage]);

  const handleNext = useCallback((): void => {
    scrollToPage(Math.min(totalPages - 1, scrollPage + 1));
  }, [scrollPage, totalPages, scrollToPage]);

  const showControls = totalPages > 1;
  const canGoPrev = scrollPage > 0;
  const canGoNext = scrollPage < totalPages - 1;

  return (
    <Box sx={disableCard ? undefined : cardSx} data-userpilot="member-carousel">
      {/* Avatar scroll row */}
      <Stack direction="row" alignItems="center" gap={0}>
        {/* Left arrow */}
        <IconButton
          onClick={handlePrev}
          disabled={!canGoPrev}
          size="small"
          aria-label="Scroll members left"
          sx={{
            opacity: showControls ? (canGoPrev ? 1 : 0.25) : 0,
            pointerEvents: showControls ? 'auto' : 'none',
            flexShrink: 0,
            ml: 0.5,
            color: 'text.secondary',
          }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        {/* Scrollable avatar row */}
        <Stack
          direction="row"
          gap={2}
          ref={scrollRef}
          sx={{ ...scrollRowSx, flex: 1 }}
        >
          {members.map((member) => {
            const isSelected = member.userId === selectedUserId;
            const hasActions = membersWithActions?.has(member.userId) ?? false;
            const { bg: color, fg } = getUserAvatarColors(
              member.userId,
              isDark
            );
            return (
              <Stack
                key={member.userId}
                alignItems="center"
                gap={0}
                sx={{ flexShrink: 0 }}
              >
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label={
                      hasActions
                        ? `${member.displayName}, action required`
                        : member.displayName
                    }
                    aria-pressed={isSelected}
                    onClick={() => onSelect(member.userId)}
                    onKeyDown={(e) =>
                      handleAvatarKeyDown(e, member.userId, onSelect)
                    }
                    sx={getAvatarSx(isSelected, color)}
                  >
                    <Typography
                      sx={{
                        ...initialsTextSx,
                        color: isSelected ? fg : color,
                      }}
                    >
                      {member.initials}
                    </Typography>
                  </Box>
                  {!actionsLoading && membersWithActions && (
                    <Box
                      aria-hidden="true"
                      data-testid={
                        hasActions
                          ? `member-action-required-${member.userId}`
                          : `member-no-action-${member.userId}`
                      }
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: hasActions
                          ? 'error.dark'
                          : 'success.dark',
                        border: '2px solid',
                        borderColor: 'background.paper',
                      }}
                    >
                      {!hasActions && (
                        <CheckIcon
                          sx={{ fontSize: 10, color: 'background.paper' }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
                <Typography sx={getNameSx(isSelected)}>
                  {member.firstName}
                </Typography>
                <Typography sx={getLastNameSx(isSelected)}>
                  {member.lastName}
                </Typography>
              </Stack>
            );
          })}
        </Stack>

        {/* Right arrow */}
        <IconButton
          onClick={handleNext}
          disabled={!canGoNext}
          size="small"
          aria-label="Scroll members right"
          sx={{
            opacity: showControls ? (canGoNext ? 1 : 0.25) : 0,
            pointerEvents: showControls ? 'auto' : 'none',
            flexShrink: 0,
            mr: 0.5,
            color: 'text.secondary',
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Pagination dots — only when content overflows */}
      {showControls && (
        <Stack
          direction="row"
          justifyContent="center"
          gap={0.5}
          sx={{ pt: 0.75, pb: 0.25 }}
          aria-hidden="true"
        >
          {Array.from({ length: totalPages }).map((_, i) => (
            <Box
              key={i}
              role="button"
              tabIndex={0}
              aria-label={`Go to page ${i + 1}`}
              onClick={() => scrollToPage(i)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  scrollToPage(i);
                }
              }}
              sx={{
                width: i === scrollPage ? 16 : 6,
                height: 6,
                borderRadius: '3px',
                backgroundColor:
                  i === scrollPage
                    ? activeDotColor
                    : 'action.disabledBackground',
                transition: 'width 0.2s, background-color 0.2s',
                cursor: i === scrollPage ? 'default' : 'pointer',
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: activeDotColor,
                  outlineOffset: 2,
                },
              }}
            />
          ))}
        </Stack>
      )}
      {children}
    </Box>
  );
});

export default MemberCarousel;
